// src/components/marketing/AudienceBuilderPage.tsx
// Track E4 — AI audience builder (A10).
//
// Prompt form at top asks the LLM for a Meta-compatible targeting spec.
// Below it, a list of past suggestions with confidence + reasoning and
// apply/dismiss actions (the "apply" just marks intent — porting the
// spec to a specific AdSet lives in E2.3 wizard).

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, Ban, Check, ChevronDown, ChevronRight,
  Loader2, RefreshCw, Users, Wand2,
} from 'lucide-react';

import {
  ApiError, applyAudience, buildAudience, dismissAudience, listAudiences,
  type AudienceSuggestion, type BuildAudienceIn, type GenerationStatus,
  type Paginated,
} from '@/lib/api_ai_marketing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props { addNotification?: Notifier; }

export function AudienceBuilderPage({ addNotification }: Props) {
  const [prompt, setPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [list, setList] = useState<Paginated<AudienceSuggestion> | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<{ applied?: boolean; dismissed?: boolean }>({ dismissed: false });
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [pending, setPending] = useState<Record<number, 'apply' | 'dismiss'>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listAudiences({ ...filter, limit: 25 });
      setList(rows);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
      addNotification?.({ type: 'error', title: 'No se pudieron cargar las sugerencias', message: msg });
    } finally {
      setLoading(false);
    }
  }, [filter, addNotification]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = prompt.trim();
    if (trimmed.length < 10 || submitting) return;

    const payload: BuildAudienceIn = { prompt: trimmed };
    setSubmitting(true);
    try {
      const row = await buildAudience(payload);
      if (row.status === 'failed') {
        addNotification?.({
          type: 'error', title: 'No se pudo proponer audiencia',
          message: row.error_message || 'El LLM no devolvió un JSON válido.',
        });
      } else {
        addNotification?.({
          type: 'success', title: 'Sugerencia generada',
          message: row.suggested_name || 'Revisa el resultado abajo.',
          duration: 3000,
        });
        // Pin it open so the user sees the reasoning immediately.
        setOpenIds((s) => new Set(s).add(row.id));
      }
      setList((prev) => prev ? {
        items: [row, ...prev.items].slice(0, 25),
        count: prev.count + 1,
      } : { items: [row], count: 1 });
      setPrompt('');
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : (err?.message || 'Error');
      addNotification?.({ type: 'error', title: 'Error al generar', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApply = async (row: AudienceSuggestion) => {
    setPending((p) => ({ ...p, [row.id]: 'apply' }));
    try {
      const updated = await applyAudience(row.id);
      updateRow(updated);
      addNotification?.({
        type: 'success',
        title: 'Marcada como aplicada',
        message: 'Cópiala al targeting de un AdSet desde el wizard de campañas.',
      });
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
      addNotification?.({ type: 'error', title: 'Error', message: msg });
    } finally {
      setPending((p) => { const { [row.id]: _d, ...rest } = p; return rest; });
    }
  };

  const handleDismiss = async (row: AudienceSuggestion) => {
    setPending((p) => ({ ...p, [row.id]: 'dismiss' }));
    try {
      const updated = await dismissAudience(row.id);
      updateRow(updated);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
      addNotification?.({ type: 'error', title: 'Error', message: msg });
    } finally {
      setPending((p) => { const { [row.id]: _d, ...rest } = p; return rest; });
    }
  };

  const updateRow = (updated: AudienceSuggestion) => {
    setList((prev) => prev ? {
      ...prev,
      items: prev.items.map((r) => r.id === updated.id ? updated : r),
    } : prev);
  };

  return (
    <div className="space-y-5">
      {/* ── Prompt form ───────────────────────────────────── */}
      <form onSubmit={submit} className="o-card p-5 space-y-3">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Users size={18} className="text-[var(--acc1,#00e5ff)]" />
          Audiencias IA
        </h1>
        <p className="text-sm text-white/60">
          Describe tu negocio y el objetivo; la IA propone un{' '}
          <strong>targeting Meta-compatible</strong> con razonamiento y confianza.
        </p>

        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder="Agencia de viajes de aventura en México. Queremos llegar a millennials que buscan experiencias fuera del turismo masivo, con gusto por naturaleza y fotografía."
          className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
        />

        <div className="flex items-center justify-between">
          <span className="text-xs text-white/40">
            Mínimo 10 caracteres · Temperatura fija en 0.3 para JSON estable.
          </span>
          <button
            type="submit"
            disabled={prompt.trim().length < 10 || submitting}
            className="px-4 py-2 rounded-md bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {submitting ? 'Generando…' : 'Proponer audiencia'}
          </button>
        </div>
      </form>

      {/* ── List ──────────────────────────────────────────── */}
      <div className="o-card p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h2 className="text-base font-semibold">Sugerencias</h2>
          <div className="flex-1" />
          <FilterPill
            active={filter.dismissed === false}
            onClick={() => setFilter({ dismissed: false })}
          >
            Activas
          </FilterPill>
          <FilterPill
            active={filter.applied === true}
            onClick={() => setFilter({ applied: true })}
          >
            Aplicadas
          </FilterPill>
          <FilterPill
            active={filter.dismissed === true}
            onClick={() => setFilter({ dismissed: true })}
          >
            Descartadas
          </FilterPill>
          <FilterPill
            active={Object.keys(filter).length === 0}
            onClick={() => setFilter({})}
          >
            Todas
          </FilterPill>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="o-btn-ghost p-1.5"
            title="Refrescar"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading && !list ? (
          <div className="py-10 text-center opacity-60">
            <Loader2 className="inline animate-spin" size={18} />
          </div>
        ) : !list || list.items.length === 0 ? (
          <div className="py-10 text-center text-sm text-white/50">
            Sin sugerencias con este filtro.
          </div>
        ) : (
          <div className="space-y-2">
            {list.items.map((row) => (
              <AudienceCard
                key={row.id}
                row={row}
                expanded={openIds.has(row.id)}
                onToggle={() => setOpenIds((s) => {
                  const next = new Set(s);
                  if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
                  return next;
                })}
                pending={pending[row.id]}
                onApply={() => handleApply(row)}
                onDismiss={() => handleDismiss(row)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Card
// ═══════════════════════════════════════════════════════════════

function AudienceCard({
  row, expanded, onToggle, pending, onApply, onDismiss,
}: {
  row: AudienceSuggestion;
  expanded: boolean;
  onToggle: () => void;
  pending?: 'apply' | 'dismiss';
  onApply: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className={`rounded-lg border overflow-hidden ${
      expanded ? 'border-[var(--acc1,#00e5ff)]/30 bg-[var(--acc1,#00e5ff)]/5' : 'border-white/10 bg-white/5'
    }`}>
      <button type="button" onClick={onToggle} className="w-full text-left px-4 py-3">
        <div className="flex items-center gap-2 text-sm">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <StatusBadge status={row.status} />
          <span className="font-medium truncate">
            {row.suggested_name || <span className="text-white/50">(sin nombre)</span>}
          </span>
          <div className="flex-1" />
          {row.confidence !== null && <ConfidenceBar value={row.confidence} />}
          {row.applied && <DispositionPill kind="applied" />}
          {row.dismissed && <DispositionPill kind="dismissed" />}
          <span className="text-xs text-white/40">{formatDate(row.created_at)}</span>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {row.status === 'failed' && row.error_message && (
            <div className="p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div className="break-words">{row.error_message}</div>
            </div>
          )}

          {row.prompt && (
            <>
              <div className="text-xs uppercase tracking-wider text-white/40">Prompt</div>
              <div className="text-sm bg-white/5 p-3 rounded-md whitespace-pre-wrap">{row.prompt}</div>
            </>
          )}

          {row.reasoning && (
            <>
              <div className="text-xs uppercase tracking-wider text-white/40">Razonamiento</div>
              <div className="text-sm bg-white/5 p-3 rounded-md whitespace-pre-wrap">{row.reasoning}</div>
            </>
          )}

          {Object.keys(row.suggested_targeting).length > 0 && (
            <>
              <div className="text-xs uppercase tracking-wider text-white/40">Targeting</div>
              <pre className="text-xs bg-white/5 p-3 rounded-md overflow-x-auto font-mono">
                {JSON.stringify(row.suggested_targeting, null, 2)}
              </pre>
            </>
          )}

          {row.status === 'completed' && !row.applied && !row.dismissed && (
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onDismiss}
                disabled={!!pending}
                className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 text-sm flex items-center gap-1"
              >
                {pending === 'dismiss' ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                Descartar
              </button>
              <button
                type="button"
                onClick={onApply}
                disabled={!!pending}
                className="px-3 py-1.5 rounded-md bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-sm font-medium flex items-center gap-1"
              >
                {pending === 'apply' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Marcar aplicada
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const color = pct >= 75 ? 'bg-emerald-400' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-1" title={`Confianza ${pct}%`}>
      <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-white/50">{pct}%</span>
    </div>
  );
}

function DispositionPill({ kind }: { kind: 'applied' | 'dismissed' }) {
  if (kind === 'applied') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-emerald-500/15 text-emerald-300">
        ✓ Aplicada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-white/10 text-white/60">
      Descartada
    </span>
  );
}

function FilterPill({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded-md text-xs ${
        active
          ? 'bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)]'
          : 'bg-white/5 text-white/60 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

const STATUS_STYLES: Record<GenerationStatus, { bg: string; label: string }> = {
  pending:   { bg: 'bg-amber-500/15 text-amber-300',     label: 'Pendiente' },
  completed: { bg: 'bg-emerald-500/15 text-emerald-300', label: 'Completado' },
  failed:    { bg: 'bg-red-500/15 text-red-300',         label: 'Falló' },
};

function StatusBadge({ status }: { status: GenerationStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium ${s.bg}`}>
      {s.label}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default AudienceBuilderPage;
